import { useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { FIELD_OPTIONS } from "../../../../CreateTicket/utils/constants";
import type { CustomFieldType } from "../../../../CreateTicket/utils/types";

function computePosition(triggerRef: React.RefObject<HTMLElement>) {
  if (!triggerRef.current)
    return {
      x: 0,
      bottom: undefined as number | undefined,
      top: 0 as number | undefined,
    };
  const rect = triggerRef.current.getBoundingClientRect();
  const menuHeight = 340;
  const gap = 4;

  const spaceBelow = window.innerHeight - rect.bottom - gap;
  const openAbove = spaceBelow < menuHeight && rect.top > spaceBelow;

  return {
    x: rect.left,
    ...(openAbove
      ? { bottom: window.innerHeight - rect.top + gap, top: undefined }
      : { top: rect.bottom + gap, bottom: undefined }),
  };
}

export interface AddCustomFieldDropdownMenuProps {
  triggerRef: React.RefObject<HTMLElement>;
  onSelect: (fieldType: CustomFieldType) => void;
  onClose: () => void;
}

export const AddCustomFieldDropdownMenu: React.FC<
  AddCustomFieldDropdownMenuProps
> = ({ triggerRef, onSelect, onClose }) => {
  const position = computePosition(triggerRef);

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const handleOverlayClick = useCallback(() => {
    onClose();
  }, [onClose]);

  return createPortal(
    <>
      <div
        className="fixed inset-0 overflow-hidden"
        style={{ zIndex: 9999998 }}
        onClick={handleOverlayClick}
        onWheel={(e) => e?.preventDefault()}
        onTouchMove={(e) => e?.preventDefault()}
      />
      <div
        className="fixed min-w-[200px] p-2 rounded-lg bg-popper-bg-fixed-white dark:bg-popper-bg-fixed-white-dk border border-border-gray dark:border-border-gray-dk shadow-xl"
        style={{
          ...(position.bottom !== undefined
            ? { bottom: position.bottom }
            : { top: position.top }),
          left: position.x,
          zIndex: 9999999,
        }}
      >
        {FIELD_OPTIONS?.map((option) => (
          <button
            key={option?.id}
            type="button"
            onClick={() => onSelect(option?.id)}
            className="flex items-center gap-2.5 w-full px-2 py-2 rounded-md cursor-pointer hover:bg-hover-effect dark:hover:bg-hover-effect-dk transition-colors"
          >
            <option.icon className={option?.iconClass} />
            <span className="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk whitespace-nowrap">
              {option?.label}
            </span>
          </button>
        ))}
      </div>
    </>,
    document.body
  );
};
